import { createFileRoute, notFound, redirect } from '@tanstack/react-router';

import { openOrganizationInThisTab } from '@/lib/org-handoff';
import { organizationsQueryOptions, userProfileQueryOptions } from '@/lib/query-options';
import type { OrganizationDataWithMembership } from '@/lib/types';

/**
 * A client by path (#6526): `/client/{slug}`.
 *
 * An organisation is addressed in the path by its slug, never as a query parameter. This route resolves the slug
 * among the person's active memberships, opens it in this tab (every request the tab sends names it, #6773; it also
 * becomes the saved selection a new tab opens on) and lands on its campaigns (#6774). A slug the person has no membership
 * on is the uniform not-found page, indistinguishable from a slug that does not exist. An app already showing
 * another organisation holds its data in memory, so the landing reloads the document in that case, as the
 * `?org=` handoff does; that handoff retires once the dashboard's links have moved here.
 */
export const Route = createFileRoute('/_authenticated/client/$slug')({
  beforeLoad: async ({ params, context }) => {
    const { queryClient } = context;
    const organizations = await queryClient.ensureQueryData(organizationsQueryOptions());
    const target = organizations.find(
      (org: OrganizationDataWithMembership) => org.membership?.status === 'active' && org.slug === params.slug,
    );
    if (!target) throw notFound();

    const userData = await queryClient.ensureQueryData(userProfileQueryOptions());
    const reloadDocument = await openOrganizationInThisTab(target, userData?.last_organization_id, queryClient);
    throw redirect({ to: '/campaigns', replace: true, reloadDocument });
  },
});
